/* Pretty Coded — storefront behaviour.
   Every feature degrades: the buy form posts natively without JS. */

(() => {
  const money = (cents) =>
    (cents / 100).toLocaleString(undefined, {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
      minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    });

  const root = () => window.Shopify?.routes?.root || '/';

  /* ---------- scroll reveal ----------
     Re-run on section load: the theme editor re-injects HTML without
     re-running page-load scripts, which would leave sections at opacity 0. */

  let observer = null;

  const reveal = (scope = document) => {
    if (!('IntersectionObserver' in window)) {
      scope.querySelectorAll('.rv').forEach((el) => el.classList.add('in'));
      return;
    }

    observer ??= new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('in');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.14 }
    );

    scope.querySelectorAll('.rv:not(.in)').forEach((el) => observer.observe(el));
  };

  /* ---------- cart ---------- */

  const drawer = () => document.getElementById('cart-drawer');

  const openDrawer = () => {
    const el = drawer();
    if (el && !el.open && typeof el.showModal === 'function') el.showModal();
  };

  const setCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
    });
  };

  const repaintDrawer = (html) => {
    if (!html) return;
    const el = drawer();
    if (!el) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const next = parsed.getElementById('cart-drawer');
    if (next) el.innerHTML = next.innerHTML;
  };

  const refresh = (data) => {
    if (typeof data?.item_count === 'number') setCount(data.item_count);
    repaintDrawer(data?.sections?.['cart-drawer']);
  };

  async function addToCart(form) {
    const button = form.querySelector('[data-add]');
    const errorBox = form.querySelector('[data-buy-error]');
    const label = button?.textContent;

    if (errorBox) errorBox.textContent = '';
    if (button) {
      button.disabled = true;
      button.textContent = 'Adding…';
    }

    try {
      // add.js returns only the added lines, so the cart fetch supplies the
      // running total and the re-rendered drawer in one go.
      const res = await fetch(`${root()}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      const data = await res.json();

      if (!res.ok) throw new Error(data.description || data.message || 'Could not add to bag.');

      const cart = await (await fetch(`${root()}cart.js?sections=cart-drawer`)).json();
      refresh(cart);
      openDrawer();
    } catch (err) {
      if (errorBox) errorBox.textContent = err.message;
      else form.submit();
    } finally {
      if (button) {
        button.disabled = false;
        button.textContent = label;
      }
    }
  }

  async function changeLine(line, quantity) {
    const res = await fetch(`${root()}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity, sections: 'cart-drawer' }),
    });
    refresh(await res.json());
  }

  async function subscribeToNewsletter(form) {
    const email = form.elements['contact[email]']?.value.trim();
    const button = form.querySelector('button[type="submit"]');
    const message = form.querySelector('[data-newsletter-message]');
    const companyId = form.dataset.klaviyoCompanyId;
    const listId = form.dataset.klaviyoListId;
    if (!email || !companyId || !listId) return form.submit();

    if (button) button.disabled = true;
    if (message) message.hidden = true;

    try {
      const res = await fetch(
        `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(companyId)}`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/vnd.api+json',
            'Content-Type': 'application/vnd.api+json',
            revision: '2026-07-15',
          },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: 'Pretty Coded homepage newsletter',
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email,
                      subscriptions: {
                        email: { marketing: { consent: 'SUBSCRIBED' } },
                      },
                    },
                  },
                },
              },
              relationships: {
                list: { data: { type: 'list', id: listId } },
              },
            },
          }),
        }
      );
      if (!res.ok) throw new Error('Klaviyo subscription failed');

      form.reset();
      if (message) {
        message.textContent = form.dataset.successMessage;
        message.hidden = false;
      }
    } catch {
      form.submit();
    } finally {
      if (button) button.disabled = false;
    }
  }

  /* ---------- contact form spam guard ---------- */

  const pageLoadedAt = Date.now();
  const MIN_FILL_SECONDS = 3;

  function rejectAsSpam(form) {
    const message = form.querySelector('[data-contact-message]');
    const honeypot = form.elements['website_url'];

    // Bots fill every field they find, including the hidden one.
    if (honeypot && honeypot.value.trim() !== '') return true;

    if (Date.now() - pageLoadedAt < MIN_FILL_SECONDS * 1000) {
      if (message) {
        message.textContent = 'That was quick — give it a moment and press Send again.';
        message.hidden = false;
      }
      return true;
    }

    if (message) message.hidden = true;
    return false;
  }

  /* ---------- tiktok carousel ---------- */

  function ttPosts(section) {
    return [...section.querySelectorAll('[data-tt-open]')].map((el) => ({
      id: el.dataset.ttId,
      url: el.dataset.ttUrl,
    }));
  }

  function openTikTokReel(section, startIndex) {
    const overlay = section.querySelector('[data-tt-overlay]');
    const reel = section.querySelector('[data-tt-reel]');
    if (!overlay || !reel) return;

    reel.innerHTML = ttPosts(section)
      .map(
        (p) =>
          `<div class="tt-slide"><blockquote class="tiktok-embed" cite="${p.url}" data-video-id="${p.id}"><section></section></blockquote></div>`
      )
      .join('');

    overlay.hidden = false;
    document.body.style.overflow = 'hidden';

    // TikTok only scans for embeds on load, so re-run it against the new nodes.
    const s = document.createElement('script');
    s.src = 'https://www.tiktok.com/embed.js';
    s.async = true;
    document.body.appendChild(s);

    const slide = reel.children[startIndex];
    if (slide) slide.scrollIntoView();
  }

  function closeTikTokReel(section) {
    const overlay = section.querySelector('[data-tt-overlay]');
    const reel = section.querySelector('[data-tt-reel]');
    if (!overlay) return;
    overlay.hidden = true;
    if (reel) reel.innerHTML = '';
    document.body.style.overflow = '';
  }

  function ttScroll(section, dir) {
    const track = section.querySelector('[data-tt-track]');
    if (!track) return;
    const card = track.querySelector('.tt-card');
    const step = card ? card.getBoundingClientRect().width + 20 : track.clientWidth;
    track.scrollBy({ left: step * dir, behavior: 'smooth' });
  }

  /* ---------- buy form ---------- */

  const syncPrice = (form) => {
    const checked = form.querySelector('input[name="id"]:checked');
    const button = form.querySelector('[data-add]');
    const backInStock = form.querySelector('[data-back-in-stock]');
    if (!checked || !button || !backInStock) return;

    const available = checked.dataset.available === 'true';
    const price = Number(checked.dataset.price || 0);

    button.hidden = !available;
    button.textContent = `Add to bag — ${money(price)}`;
    backInStock.hidden = available;

    /* The sticky bar mirrors the form rather than holding its own state, so the
       two can't disagree about which variant is selected. */
    const bar = document.querySelector('[data-sticky-buy]');
    if (!bar) return;

    const label = checked.closest('.opt')?.querySelector('.opt-main b');
    bar.querySelector('[data-sticky-label]').textContent =
      label?.childNodes[0]?.textContent.trim() || '';
    bar.querySelector('[data-sticky-price]').textContent = money(price);
    bar.querySelector('[data-sticky-add]').hidden = !available;
    bar.querySelector('[data-sticky-back-in-stock]').hidden = available;
  };

  const requestBackInStock = (trigger) => {
    const form = document.querySelector('[data-buy-form]');
    const variant = form?.querySelector('input[name="id"]:checked')?.value;
    const productUrl = trigger.dataset.productUrl;
    if (!variant || !productUrl) return;

    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('variant', variant);
    url.searchParams.set('notify', '1');
    window.location.assign(url);
  };

  const openRequestedBackInStockForm = () => {
    const url = new URL(window.location.href);
    if (url.searchParams.get('notify') !== '1') return;

    const formId = document.querySelector('[data-klaviyo-form-id]')?.dataset.klaviyoFormId;
    if (!formId) return;

    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts += 1;
      const klaviyoReady =
        typeof window.klaviyo?.openForm === 'function' &&
        document.getElementById('klaviyo-bis-button-container');
      if (!klaviyoReady) {
        if (attempts >= 50) window.clearInterval(timer);
        return;
      }

      window.clearInterval(timer);
      window.klaviyo.openForm(formId);
      url.searchParams.delete('notify');
      window.history.replaceState({}, '', url);
    }, 100);
  };

  /* ---------- wiring ---------- */

  document.addEventListener('change', (e) => {
    const radio = e.target.closest('input[name="id"]');
    if (radio) syncPrice(radio.form);
  });

  document.addEventListener('submit', (e) => {
    const contact = e.target.closest('[data-contact-form]');
    if (contact && rejectAsSpam(contact)) {
      e.preventDefault();
      return;
    }

    const newsletter = e.target.closest('[data-newsletter-form]');
    if (newsletter) {
      e.preventDefault();
      subscribeToNewsletter(newsletter);
      return;
    }

    const form = e.target.closest('[data-buy-form]');
    if (!form) return;
    e.preventDefault();
    addToCart(form);
  });

  document.addEventListener('click', (e) => {
    const ttOpen = e.target.closest('[data-tt-open]');
    if (ttOpen) {
      const section = ttOpen.closest('[data-tiktok-section]');
      if (section) openTikTokReel(section, Number(ttOpen.dataset.ttOpen) || 0);
      return;
    }

    const ttClose = e.target.closest('[data-tt-close]');
    if (ttClose) {
      const section = ttClose.closest('[data-tiktok-section]');
      if (section) closeTikTokReel(section);
      return;
    }

    const ttPrev = e.target.closest('[data-tt-prev]');
    if (ttPrev) {
      ttScroll(ttPrev.closest('[data-tiktok-section]'), -1);
      return;
    }

    const ttNext = e.target.closest('[data-tt-next]');
    if (ttNext) {
      ttScroll(ttNext.closest('[data-tiktok-section]'), 1);
      return;
    }

    const backInStock = e.target.closest('[data-back-in-stock], [data-sticky-back-in-stock]');
    if (backInStock) {
      e.preventDefault();
      requestBackInStock(backInStock);
      return;
    }

    if (e.target.closest('[data-sticky-add]')) {
      e.preventDefault();
      document.querySelector('[data-buy-form]')?.requestSubmit();
      return;
    }

    if (e.target.closest('[data-cart-open]')) {
      e.preventDefault();
      openDrawer();
      return;
    }

    if (e.target.closest('[data-cart-close]')) {
      e.preventDefault();
      drawer()?.close();
      return;
    }

    const remove = e.target.closest('[data-line-remove]');
    if (remove) {
      e.preventDefault();
      changeLine(Number(remove.dataset.lineRemove), 0);
    }
  });

  /* Clicking the backdrop closes the drawer. */
  document.addEventListener('click', (e) => {
    const el = drawer();
    if (!el?.open || e.target !== el) return;
    el.close();
  });

  /* ---------- contents list ----------
     Policy text is written in the admin, so the sidebar is derived from
     whatever h2s the copy happens to contain rather than maintained by hand. */

  const slug = (text) =>
    text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .slice(0, 60);

  const buildToc = (scope = document) => {
    scope.querySelectorAll('[data-toc]').forEach((nav) => {
      const source = nav.closest('.util-cols')?.querySelector('[data-toc-source]');
      if (!source) return;

      const headings = [...source.querySelectorAll('h2')];
      nav.textContent = '';
      if (headings.length < 2) return;

      const used = new Set();
      const links = headings.map((h) => {
        if (!h.id) {
          let id = slug(h.textContent) || 'section';
          let n = 2;
          while (used.has(id) || document.getElementById(id)) id = `${slug(h.textContent)}-${n++}`;
          h.id = id;
        }
        used.add(h.id);

        const a = document.createElement('a');
        a.href = `#${h.id}`;
        a.textContent = h.textContent;
        nav.appendChild(a);
        return a;
      });

      if (!('IntersectionObserver' in window)) return;

      const spy = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            links.forEach((a) => a.removeAttribute('aria-current'));
            const active = links.find((a) => a.hash === `#${entry.target.id}`);
            active?.setAttribute('aria-current', 'true');
          });
        },
        { rootMargin: '-90px 0px -70% 0px' }
      );

      headings.forEach((h) => spy.observe(h));
    });
  };

  /* ---------- policy pages ----------
     Shopify renders /policies/* with its own markup and ignores theme
     templates, so the styling hooks are grafted on here instead. */

  const adoptPolicyPage = () => {
    const container = document.querySelector('.shopify-policy__container');
    if (!container || container.dataset.adopted) return;
    container.dataset.adopted = 'true';

    document.getElementById('main')?.classList.add('util');

    // CSS hides this, but it's a second h1 until it's actually gone.
    container.querySelector('.shopify-policy__title')?.remove();

    const body = container.querySelector('.shopify-policy__body');
    const rte = body?.querySelector('.rte');
    if (!body || !rte) return;

    rte.classList.add('prose');
    rte.setAttribute('data-toc-source', '');

    /* The intro sits in the policy body, but the design puts it in the header.
       Moving it keeps one copy of the text rather than printing it twice. */
    const lede = rte.querySelector('p');
    const head = document.querySelector('.util-head');
    if (lede && head) {
      lede.classList.add('util-lede');
      head.append(lede);
    }

    body.classList.add('util-body', 'wrap');
    const cols = document.createElement('div');
    cols.className = 'util-cols';
    const nav = document.createElement('nav');
    nav.className = 'toc';
    nav.setAttribute('data-toc', '');
    nav.setAttribute('aria-label', 'On this page');

    body.insertBefore(cols, rte);
    cols.append(nav, rte);
  };

  const init = (scope) => {
    adoptPolicyPage();
    reveal(scope);
    buildToc(scope);
    scope.querySelectorAll('[data-buy-form]').forEach(syncPrice);
    if (scope === document) openRequestedBackInStockForm();
  };

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('[data-tt-overlay]:not([hidden])').forEach((overlay) => {
      const section = overlay.closest('[data-tiktok-section]');
      if (section) closeTikTokReel(section);
    });
  });

  document.addEventListener('DOMContentLoaded', () => init(document));
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
